import { useStacklineTheme } from '@stackline/ui';
import React from 'react';
import {
  ICON_STYLES,
  TopicFilter
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Filters/constants';
import {
  useTopicBuilderActions,
  useTopicBuilderFilters
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Store/useTopicBuilderStore';
import CheckboxItem from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/CheckboxItem';
import GenericSidebarFilter, {
  GenericSidebarFilterProps
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/GenericSidebarFilter';
import { SlSidebarScrollableContainer } from 'src/components/BeaconV2Components/SlSidebar';
import {
  DisabledCheckbox,
  SlDisabledExcludeSelectedCheckbox,
  SlExcludeSelectedCheckbox,
  SlSelectedCheckbox
} from 'src/components/SvgIcons';
import { useAppSelector } from 'src/utils/Hooks';

interface CategorySidebarFilterProps extends GenericSidebarFilterProps {
  readOnly?: boolean;
}

const CategorySidebarFilter = (props: CategorySidebarFilterProps) => {
  const theme = useStacklineTheme();
  const { filters } = useTopicBuilderFilters();
  const { setFilters } = useTopicBuilderActions();

  const includedCategories = filters[TopicFilter.IncludedCategoryIds];
  const excludedCategories = filters[TopicFilter.ExcludedCategoryIds];

  const isIncluding =
    includedCategories.length > 0 || (excludedCategories.length === 0 && includedCategories.length === 0);
  const selectedCategories = isIncluding ? includedCategories : excludedCategories;

  const shouldRender = selectedCategories?.length > 0 || !props.readOnly;

  const categoryOptions = useAppSelector((state) =>
    state.categories.map((category) => ({
      ...category,
      selected: selectedCategories.some((selectedCategory) => Number(selectedCategory) === category.id)
    }))
  );

  /**
   * Search input for filtering down categories
   */
  const [currentInput, setCurrentInput] = React.useState<string>('');

  /**
   * Toggle mode for include/exclude; determines which icon to render in the renderItems function.
   */
  const [toggleMode, setToggleMode] = React.useState<'include' | 'exclude'>(isIncluding ? 'include' : 'exclude');

  React.useEffect(() => {
    setToggleMode(isIncluding ? 'include' : 'exclude');
  }, [isIncluding]);

  /**
   * Handles the selection of a category.
   * Updates the selected state of the category and filters down the available subcategories based on the selected categories.
   */
  const handleItemSelect = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      const id = Number(value);
      switch (toggleMode) {
        case 'include':
          setFilters(({ toggle }) => toggle({ key: TopicFilter.IncludedCategoryIds, value: id }));
          break;
        case 'exclude':
          setFilters(({ toggle }) => toggle({ key: TopicFilter.ExcludedCategoryIds, value: id }));
          break;
        default:
      }
    },
    [toggleMode, setFilters]
  );

  const getIcon = React.useCallback(() => {
    switch (toggleMode) {
      case 'include': {
        if (props.readOnly) {
          return <DisabledCheckbox style={ICON_STYLES} />;
        } else {
          return <SlSelectedCheckbox style={ICON_STYLES} />;
        }
      }

      case 'exclude': {
        if (props.readOnly) {
          return <SlDisabledExcludeSelectedCheckbox style={ICON_STYLES} />;
        } else {
          return <SlExcludeSelectedCheckbox style={ICON_STYLES} />;
        }
      }
      default:
        return null;
    }
  }, [toggleMode, props.readOnly]);

  /**
   * Renders the list of items as checkboxes.
   */
  const renderItems = React.useCallback(() => {
    return (
      <SlSidebarScrollableContainer>
        {categoryOptions
          ?.filter((category) => category?.name?.toLowerCase().includes(currentInput.toLowerCase()))
          .sort((a, b) => (a.selected === b.selected ? 0 : a.selected ? -1 : 1)) // sort by checked items
          .map((category, index) => (
            <CheckboxItem
              disabled={props.readOnly}
              onClick={handleItemSelect}
              value={category.id}
              key={index}
              label={category.name}
              theme={theme}
              checked={category.selected}
              checkedIcon={getIcon()}
            />
          ))}
      </SlSidebarScrollableContainer>
    );
  }, [categoryOptions, currentInput, theme, handleItemSelect, getIcon, props.readOnly]);

  /**
   * Handles the change of the input to filter down the categories.
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentInput(e.target.value);
  };

  /**
   * Clears the selected categories and subcategories.
   */
  const handleClear = () => {
    switch (toggleMode) {
      case 'include':
        setFilters(({ clear }) => clear({ key: TopicFilter.IncludedCategoryIds }));
        break;
      case 'exclude':
        setFilters(({ clear }) => clear({ key: TopicFilter.ExcludedCategoryIds }));
        break;
      default:
    }

    setCurrentInput('');
  };

  const handleToggle = (toggle: 'include' | 'exclude') => {
    setToggleMode(toggle);

    switch (toggle) {
      case 'include':
        setFilters(({ replace }) =>
          replace({ target: TopicFilter.IncludedCategoryIds, source: TopicFilter.ExcludedCategoryIds })
        );
        break;
      case 'exclude':
        setFilters(({ replace }) =>
          replace({ target: TopicFilter.ExcludedCategoryIds, source: TopicFilter.IncludedCategoryIds })
        );
        break;
      default:
    }
  };

  return (
    <>
      {shouldRender && (
        <GenericSidebarFilter
          {...props}
          includeInput={!props.readOnly}
          renderItems={renderItems}
          includeClear={selectedCategories.length > 0 && !props.readOnly}
          includeToggles={!props.readOnly}
          handleClear={handleClear}
          handleToggle={handleToggle}
          toggleMode={toggleMode}
          sidebarInputProps={{
            ...props.sidebarInputProps,
            value: currentInput,
            onChange: handleChange
          }}
        />
      )}
    </>
  );
};

export default CategorySidebarFilter;
